import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, ChevronRight, Copy, GraduationCap, LifeBuoy, Package, Printer, RefreshCw } from "lucide-react";
import { commerceKey, getCommerceOrder, listCommerceOrders, resumeCommerceOrder } from "lib/siteCommerceApi";
import { formatCartMoney } from "lib/paperworkCart";
import { BANK_FIELDS, validOrder } from "pages/StoreOrderPage";
import { safeStripeCheckoutUrl } from "pages/StoreCheckoutPage";
import OrderDelivery from "components/commerce/OrderDelivery";
import { REFUND_RULE } from "components/ios/Alert";
import { SplitView, useSplitClose, useSplitLink } from "components/ios/SplitView";

const isCourse = (o) => o.kind === "course_enrollment";
export const shortRef = (o) => `MAG-${String(o.id || "").slice(0, 8).toUpperCase()}`;
const day = (ms) => (ms ? new Date(ms).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "");
export const orderTitle = (o) => o.items.map((i) => (i.type ? i.name : `${i.name || i.packageId} paperwork`)).join(" + ");
const orderSub = (o) => (isCourse(o) ? "Course" : o.items.map((i) => [i.stateLabel || i.state, i.equipmentLabel].filter(Boolean).join(" · ")).join(" + "));

// Buyer-facing status word and tone (word + dot, never colour alone).
export function buyerStatus(o) {
  if (o.status === "awaiting_transfer") return ["Waiting for your bank transfer", "needs"];
  if (o.status === "pending") return ["Payment started", "progress"];
  if (o.status === "review_required") return ["Payment under review", "bad"];
  if (o.status === "payment_failed") return ["Payment not completed", "bad"];
  if (o.status === "expired") return ["Expired", "closed"];
  if (o.status === "cancelled") return ["Cancelled", "closed"];
  if (isCourse(o)) return ["Access granted", "ok"];
  return o.fulfillmentStatus === "released" ? ["Ready to download", "needs"] : ["Being prepared", "progress"];
}

// Step tracker: paperwork 4 steps, courses 3. Returns [steps, index of current step].
export function trackerOf(o) {
  const steps = isCourse(o) ? ["Ordered", "Payment confirmed", "Access granted"] : ["Ordered", "Payment confirmed", "Being prepared", "Ready to download"];
  const paid = o.status === "paid";
  const current = !paid ? 1 : isCourse(o) ? 3 : o.fulfillmentStatus === "released" ? 4 : 2;
  return [steps, current];
}
export function Tracker({ order, compact, labels }) {
  const [own, current] = trackerOf(order), steps = labels && labels.length === own.length ? labels : own;
  const halted = ["review_required", "payment_failed", "expired", "cancelled"].includes(order.status);
  return (
    <ol className={`ca-tracker ${compact ? "ca-tracker-compact" : ""}`} aria-label="Order progress">
      {steps.map((label, i) => {
        const state = i < current ? "done" : i === current && !halted ? "current" : "todo";
        return (
          <li key={label} data-state={state} aria-current={state === "current" ? "step" : undefined}>
            <span className="ca-step-dot">{state === "done" ? <Check size={12} strokeWidth={3} aria-hidden="true" /> : null}</span>
            <span className="ca-step-label">{label}</span>
          </li>
        );
      })}
    </ol>
  );
}

export default function Orders() {
  const { orderId } = useParams();
  const close = useSplitClose("/customer/access/orders");
  // A refreshed or continued order updates its row in the list beside it.
  const [changed, setChanged] = useState(0);
  const onChanged = useCallback(() => setChanged((n) => n + 1), []);
  return (
    <SplitView
      open={Boolean(orderId)}
      list={<OrderList selectedId={orderId} changed={changed} />}
      detail={orderId ? <OrderDetail key={orderId} orderId={orderId} onChanged={onChanged} /> : null}
      detailKey={orderId}
      title={orderId ? `Order ${shortRef({ id: orderId })}` : "Order"}
      onClose={close}
      closeLabel="Close order"
      backLabel="Orders"
      splitAt={860}
    />
  );
}

function OrderList({ selectedId, changed = 0 }) {
  const link = useSplitLink(selectedId);
  const [state, setState] = useState({ loading: true, items: [] }), [tick, setTick] = useState(0);
  useEffect(() => {
    const c = new AbortController();
    // Reloads keep the current rows on screen until the fresh list arrives.
    setState((prev) => (prev.items?.length ? { ...prev, loading: false, refreshing: true } : { loading: true, items: [] }));
    listCommerceOrders(c.signal).then((d) => {
      if (c.signal.aborted) return;
      if (!Array.isArray(d?.items) || d.items.some((o) => !validOrder(o, o.id))) throw new Error("Your orders could not be verified. Please refresh.");
      setState({ items: [...d.items].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)) });
    }).catch((e) => { if (!c.signal.aborted) setState({ items: [], error: e.message || "Your orders could not be loaded." }); });
    return () => c.abort();
  }, [tick, changed]);
  return (
    <section aria-labelledby="ca-orders-title">
      <div className="ca-section-head">
        <h2 id="ca-orders-title" className="ios-group-header"><span>Your orders</span><span>Newest first</span></h2>
        <button type="button" className="ios-btn ios-btn-plain" onClick={() => setTick((t) => t + 1)}><RefreshCw size={15} aria-hidden="true" /> Refresh</button>
      </div>
      {state.loading ? <p className="ios-group-footer" role="status">Loading your orders…</p>
        : state.error ? <p className="ca-notice" role="alert">{state.error}</p>
        : !state.items.length ? <p className="ca-notice">No orders yet. Orders you place while signed in, or with this email, appear here.</p>
        : (
          <ul className="ios-group">
            {state.items.map((o) => {
              const [word, tone] = buyerStatus(o);
              return (
                <li key={o.id}>
                  <Link className="ios-row" to={`/customer/access/orders/${o.id}`}
                    aria-current={o.id === selectedId ? "true" : undefined} {...link}>
                    <span className="ios-tile" data-tint={isCourse(o) ? "purple" : "gold"}>{isCourse(o) ? <GraduationCap aria-hidden="true" /> : <Package aria-hidden="true" />}</span>
                    <span className="ios-row-main"><span className="ios-row-title">{orderTitle(o)}</span><span className="ios-row-sub">{shortRef(o)} · {day(o.createdAt)} · {formatCartMoney(o.totalMinor)}</span></span>
                    <span className="ios-capsule" data-tone={tone}>{word}</span>
                    <ChevronRight size={16} className="ios-chevron" aria-hidden="true" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      <p className="ios-group-footer">{REFUND_RULE}</p>
    </section>
  );
}

function CopyButton({ value, label, text = "Copy" }) {
  const [done, setDone] = useState(false);
  return (
    <button type="button" className="ios-btn ios-btn-tinted ca-copy" aria-label={`Copy ${label}`}
      onClick={async () => { try { await navigator.clipboard.writeText(value); setDone(true); setTimeout(() => setDone(false), 1600); } catch { /* Copy unavailable. */ } }}>
      {done ? <Check size={15} aria-hidden="true" /> : <Copy size={15} aria-hidden="true" />}
      <span className="ca-copy-word">{done ? "Copied" : text}</span>
    </button>
  );
}

function OrderDetail({ orderId, onChanged }) {
  const [state, setState] = useState({ loading: true }), [tick, setTick] = useState(0);
  const [resuming, setResuming] = useState(false), [resumeError, setResumeError] = useState("");
  const key = useRef(null);
  useEffect(() => {
    const c = new AbortController();
    setState({ loading: true });
    getCommerceOrder(orderId, c.signal).then((d) => {
      if (c.signal.aborted) return;
      if (!validOrder(d?.order, orderId)) throw new Error("This order could not be verified. Please refresh.");
      setState({ order: d.order });
    }).catch((e) => { if (!c.signal.aborted) setState({ error: e.message || "This order could not be loaded." }); });
    return () => c.abort();
  }, [orderId, tick]);
  const o = state.order;
  async function continuePayment() {
    if (resuming || !o) return;
    if (!key.current) key.current = commerceKey();
    setResuming(true); setResumeError("");
    try {
      const r = await resumeCommerceOrder(orderId, key.current);
      if (!validOrder(r?.order, orderId)) throw new Error("This order could not be verified for payment.");
      setState({ order: r.order });
      onChanged?.();
      if (r.order.status === "paid") return;
      const url = safeStripeCheckoutUrl(r.checkoutUrl);
      if (!url) throw new Error("A secure payment link is unavailable for this order. Refresh or contact us.");
      window.location.assign(url);
    } catch (e) { setResumeError(e.message || "Payment could not be opened."); } finally { setResuming(false); }
  }
  if (state.loading) return <><Link className="ca-back" to="/customer/access/orders">‹ Orders</Link><h1>Order</h1><p className="ios-group-footer" role="status">Checking your order…</p></>;
  if (state.error) return <><Link className="ca-back" to="/customer/access/orders">‹ Orders</Link><h1>Order</h1><p className="ca-notice" role="alert">{state.error}</p></>;
  const [word, tone] = buyerStatus(o), bank = o.bankInstructions;
  const fields = bank ? BANK_FIELDS.filter(([k]) => bank[k]) : [];
  const allDetails = fields.map(([k, label]) => `${label}: ${bank[k]}`).join("\n") + `\nAmount: ${formatCartMoney(o.totalMinor)}`;
  return (
    <div className="ca-order">
      <Link className="ca-back" to="/customer/access/orders">‹ Orders</Link>
      <h1>Order {shortRef(o)}</h1>
      <section className="ios-card ca-order-head" aria-labelledby="ca-order-title">
        <div className="ca-order-top">
          <span className="ios-tile" data-tint={isCourse(o) ? "purple" : "gold"}>{isCourse(o) ? <GraduationCap aria-hidden="true" /> : <Package aria-hidden="true" />}</span>
          <div className="ios-row-main">
            <h2 id="ca-order-title" className="ios-headline">{orderTitle(o)}</h2>
            <p className="ios-footnote">{orderSub(o)} · {formatCartMoney(o.totalMinor)}</p>
          </div>
          <span className="ios-capsule" data-tone={tone}>{word}</span>
        </div>
        <Tracker order={o} />
        <p className="ios-footnote ca-order-ref">Order {shortRef(o)} · placed {day(o.createdAt)}{o.paidAt ? ` · paid ${day(o.paidAt)}` : ""} <CopyButton value={o.reference || o.id} label="order reference" /></p>
        {!isCourse(o) && o.status === "paid" && o.fulfillmentStatus !== "released" && (
          <p className="ios-footnote">We prepare your files by hand. They appear here when they're ready.</p>
        )}
      </section>
      {o.provider === "swift" && o.status === "awaiting_transfer" && (
        <section className="ios-card ca-bank" aria-labelledby="ca-bank-title">
          <div className="ca-bank-head">
            <h3 id="ca-bank-title" className="ios-headline">Bank transfer details</h3>
            <div className="ca-bank-actions">
              {bank && <CopyButton value={allDetails} label="all bank details" text="Copy all" />}
              <button type="button" className="ios-btn ios-btn-gray" onClick={() => window.print()}><Printer size={15} aria-hidden="true" /> Print</button>
            </div>
          </div>
          {bank ? (
            <>
              <p className="ios-footnote">Transfer <strong>{formatCartMoney(o.totalMinor)}</strong> and use exactly this payment reference. We check every transfer by hand.</p>
              <ul className="ios-group ca-bank-list">
                {fields.map(([k, label]) => (
                  <li key={k} className={k === "paymentReference" ? "ca-bank-ref" : undefined}>
                    <div className="ios-row">
                      <span className="ios-row-main"><span className="ios-row-sub">{label}</span><span className="ca-bank-value">{bank[k]}</span></span>
                      <CopyButton value={bank[k]} label={label} />
                    </div>
                  </li>
                ))}
              </ul>
              {bank.instructions && <p className="ios-footnote">{bank.instructions}</p>}
            </>
          ) : <p className="ca-notice">Bank details are unavailable right now. Contact us with your order reference before sending a transfer.</p>}
        </section>
      )}
      {o.provider === "stripe" && ["pending", "payment_failed"].includes(o.status) && (
        <div className="ca-order-actions">
          <button type="button" className="ios-btn" disabled={resuming} onClick={continuePayment}>{resuming ? "Opening secure payment…" : "Continue to payment"}</button>
          {resumeError && <p className="ca-notice" role="alert">{resumeError}</p>}
        </div>
      )}
      {!isCourse(o) && o.status === "paid" && <div className="ca-delivery"><OrderDelivery key={o.id} orderId={o.id} /></div>}
      {isCourse(o) && o.status === "paid" && <Link className="ios-btn" to="/customer/access/courses">Open your courses</Link>}
      <ul className="ios-group ca-order-links">
        <li><button type="button" className="ios-row" onClick={() => { setTick((t) => t + 1); onChanged?.(); }}><span className="ios-tile" data-tint="gray"><RefreshCw aria-hidden="true" /></span><span className="ios-row-main"><span className="ios-row-title">Refresh status</span></span></button></li>
        <li><Link className="ios-row" to="/customer/access/support"><span className="ios-tile" data-tint="teal"><LifeBuoy aria-hidden="true" /></span><span className="ios-row-main"><span className="ios-row-title">Get help with this order</span><span className="ios-row-sub">Mention {shortRef(o)}</span></span><ChevronRight size={16} className="ios-chevron" aria-hidden="true" /></Link></li>
      </ul>
      <p className="ios-group-footer">{REFUND_RULE}</p>
    </div>
  );
}
